import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { http, apiErr } from "@/lib/api";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Checkbox } from "@/components/ui/checkbox";
import { Loading, EmptyState, PriorityDot } from "@/components/common";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter } from "@/components/ui/dialog";
import { toast } from "sonner";
import { BellRing, Plus, Trash2 } from "lucide-react";

const TYPES = ["richiamo", "controllo preventivo", "sopralluogo", "ricontatto preventivo", "montaggio"];
const TY_KEY = { "richiamo": "ty_richiamo", "controllo preventivo": "ty_controllo_preventivo", "sopralluogo": "ty_sopralluogo", "ricontatto preventivo": "ty_ricontatto", "montaggio": "ty_montaggio" };

export default function Promemoria() {
  const [list, setList] = useState(null);
  const [open, setOpen] = useState(false);
  const [f, setF] = useState({ title: "", type: "richiamo", due_date: "", priority: "Media" });
  const { t, i18n } = useTranslation();

  const load = () => http.get("/tasks").then((r) => setList(r.data)).catch((e) => toast.error(apiErr(e)));
  useEffect(() => { load(); }, []);

  const create = async () => { try { await http.post("/tasks", f); toast.success(t("promemoria.created")); setOpen(false); setF({ title: "", type: "richiamo", due_date: "", priority: "Media" }); load(); } catch (e) { toast.error(apiErr(e)); } };
  const toggle = async (tk) => { try { await http.put(`/tasks/${tk.id}`); load(); } catch (e) { toast.error(apiErr(e)); } };
  const del = async (id) => { try { await http.delete(`/tasks/${id}`); load(); } catch (e) { toast.error(apiErr(e)); } };

  return (
    <div className="max-w-3xl mx-auto p-4 sm:p-6 space-y-5">
      <div className="flex items-center justify-between">
        <h1 className="font-heading text-2xl font-extrabold text-[#0F172A]">{t("promemoria.title")}</h1>
        <Dialog open={open} onOpenChange={setOpen}>
          <DialogTrigger asChild><Button data-testid="new-task-btn" className="bg-[#0F172A] gap-1.5"><Plus className="w-4 h-4" /> {t("promemoria.new")}</Button></DialogTrigger>
          <DialogContent><DialogHeader><DialogTitle>{t("promemoria.new")}</DialogTitle></DialogHeader>
            <div className="space-y-3">
              <div><Label>{t("promemoria.text")}*</Label><Input data-testid="task-title" value={f.title} onChange={(e) => setF({ ...f, title: e.target.value })} className="mt-1" /></div>
              <div className="grid grid-cols-2 gap-3">
                <div><Label>{t("promemoria.type")}</Label><Select value={f.type} onValueChange={(v) => setF({ ...f, type: v })}><SelectTrigger className="mt-1"><SelectValue /></SelectTrigger><SelectContent>{TYPES.map((ty) => <SelectItem key={ty} value={ty}>{t(`promemoria.${TY_KEY[ty]}`)}</SelectItem>)}</SelectContent></Select></div>
                <div><Label>{t("promemoria.priority")}</Label><Select value={f.priority} onValueChange={(v) => setF({ ...f, priority: v })}><SelectTrigger className="mt-1"><SelectValue /></SelectTrigger><SelectContent>{[["Alta", "alta"], ["Media", "media"], ["Bassa", "bassa"]].map(([p, pk]) => <SelectItem key={p} value={p}>{t(`priority.${pk}`)}</SelectItem>)}</SelectContent></Select></div>
              </div>
              <div><Label>{t("promemoria.due")}</Label><Input type="date" value={f.due_date} onChange={(e) => setF({ ...f, due_date: e.target.value })} className="mt-1" /></div>
            </div>
            <DialogFooter><Button data-testid="save-task-btn" onClick={create} disabled={!f.title}>{t("common.save")}</Button></DialogFooter>
          </DialogContent>
        </Dialog>
      </div>

      {!list ? <Loading /> : list.length === 0 ? <EmptyState icon={BellRing} title={t("promemoria.empty")} hint={t("promemoria.empty_hint")} /> : (
        <div className="space-y-2">
          {list.map((tk) => (
            <Card key={tk.id} className="p-3 border-slate-200 flex items-center gap-3" data-testid="task-card">
              <Checkbox checked={tk.done} onCheckedChange={() => toggle(tk)} data-testid="task-checkbox" />
              <PriorityDot priority={tk.priority} />
              <div className="flex-1 min-w-0">
                <div className={`font-medium ${tk.done ? "line-through text-slate-400" : "text-[#0F172A]"}`}>{tk.title}</div>
                <div className="text-xs text-slate-500">{t(`promemoria.${TY_KEY[tk.type]}`, tk.type)}{tk.due_date && ` · ${t("promemoria.due_short")} ${new Date(tk.due_date).toLocaleDateString(i18n.language)}`}</div>
              </div>
              <Button size="icon" variant="ghost" className="text-red-500" onClick={() => del(tk.id)}><Trash2 className="w-4 h-4" /></Button>
            </Card>
          ))}
        </div>
      )}
    </div>
  );
}
